// i18n liviano del inspector — dos idiomas, dict plano, sin dependencias.
//
// Preferencia en localStorage (`authsys-inspector-lang`); si no hay, se
// detecta del browser (es* → es, resto → en). Interpolación con {var}.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type Lang = 'es' | 'en';

const STORAGE_KEY = 'authsys-inspector-lang';

const DICT: Record<Lang, Record<string, string>> = {
  es: {
    // Shell
    'shell.envBadge': 'entorno dev',
    'shell.user': 'USER',
    'shell.partner': 'PARTNER',
    'shell.token': 'TOKEN',
    'shell.tokenTitle': 'expiración del access token (se renueva solo con el refresh token)',
    'shell.utcTitle': 'reloj UTC',
    'shell.run': 'RUN',
    'shell.hold': 'HOLD',
    'shell.pauseTitle': 'pausar auto-refresh',
    'shell.resumeTitle': 'reanudar auto-refresh',
    'shell.logout': 'Salir',
    'shell.langTitle': 'idioma / language',
    'shell.footer':
      'Herramienta de integración — solo entorno de pruebas. Muestra únicamente los datos de la cuenta con la que iniciaste sesión.',
    'shell.skip': 'Saltar al contenido',

    // Login
    'login.tagline': 'consola de integración — solo entorno dev',
    'login.email': 'Email del usuario de prueba',
    'login.emailPlaceholder': 'tester@tu-ott.com',
    'login.password': 'Contraseña',
    'login.partner': 'Partner ID',
    'login.submit': 'Iniciar sesión',
    'login.submitting': 'Autenticando…',
    'login.hint':
      'Ingresá con las credenciales de un usuario común del sistema de auth. Vas a ver todos los datos que AuthSys registra sobre esa cuenta, en vivo.',
    'login.consentTitle': 'Términos y condiciones pendientes',
    'login.consentBody':
      'Esta cuenta tiene pendiente aceptar la versión {version} de los T&C del partner. Es el mismo interstitial que ve el usuario final en el login.',
    'login.consentLink': 'Leer los términos ({version})',
    'login.consentAccept': 'Aceptar y entrar',
    'login.consentAccepting': 'Registrando…',
    'login.consentCancel': 'Cancelar',
    'login.errGeneric': 'Falló el login ({code}): {msg}',
    'login.errConsent': 'No se pudo registrar la aceptación ({code}). Reintentá el login.',
    'err.invalid_credentials': 'Email o contraseña incorrectos.',
    'err.user_blocked': 'La cuenta está bloqueada. Verificalo después en el panel de audit.',
    'err.email_not_verified': 'El email de la cuenta no está verificado todavía.',
    'err.rate_limited': 'Demasiados intentos. Esperá un momento y reintentá.',
    'err.network_error': 'No se pudo llegar al API. ¿Está corriendo el entorno?',
    'err.partner_suspended': 'El partner está suspendido en este entorno.',
    'err.session_expired': 'La sesión expiró — reingresá.',

    // SectionPanel
    'panel.loading': 'cargando…',
    'panel.unavailable': 'SECCIÓN NO DISPONIBLE — {error}',
    'panel.lastFetch': 'último fetch',

    // Titles
    't.identity': 'Identidad',
    't.currentSession': 'Sesión actual',
    't.entitlements': 'Entitlements',
    't.tos': 'T&C',
    't.sessions': 'Sesiones',
    't.devices': 'Dispositivos activos',
    't.deviceCodes': 'Device codes (TV)',
    't.subscriptions': 'Suscripciones',
    't.payments': 'Pagos',
    't.emailTokens': 'Tokens de email',
    't.tosSignatures': 'Firmas T&C',
    't.audit': 'Audit log',
    't.actions': 'Acciones de ensayo',

    // KV labels
    'kv.userId': 'user_id',
    'kv.display': 'display',
    'kv.email': 'email',
    'kv.status': 'estado',
    'kv.emailVerif': 'email verif.',
    'kv.createdAt': 'alta',
    'kv.session': 'session',
    'kv.client': 'client',
    'kv.device': 'device',
    'kv.scopes': 'scopes',
    'kv.partner': 'partner',
    'kv.subscription': 'suscripción',
    'kv.plan': 'plan',
    'kv.maxStreams': 'streams máx',
    'kv.maxDevices': 'devices máx',
    'kv.pendingPayment': 'pago pend.',
    'kv.currentVersion': 'vigente',
    'kv.acceptedVersion': 'aceptada',
    'kv.graceUntil': 'gracia hasta',
    'kv.noVersions': 'sin versiones',

    // Common cell values
    'val.yes': 'sí',
    'val.no': 'no',
    'val.on': 'exigida',
    'val.off': 'no exigida',

    // Ventana de gracia de verificación (migr. 0037)
    't.grace': 'Gracia de verificación',
    'grace.kv.gate': 'verificación',
    'grace.kv.verified': 'email verificado',
    'grace.kv.planHours': 'horas del plan',
    'grace.kv.remaining': 'queda',
    'grace.kv.verdict': 'login ahora',
    'grace.none': 'sin ventana',
    'grace.expired': 'vencida',
    'grace.wouldPass': 'entra',
    'grace.wouldFail': 'rebota',
    'grace.presetMin': '{n} min',
    'grace.presetHour': '{n} h',
    'grace.set.hint': 'Reemplaza la ventana por una nueva que arranca ahora.',
    'grace.expire': 'Vencer',
    'grace.expire.hint': 'Deja la ventana vencida al instante — el próximo login rebota y el refresh corta la sesión.',
    'grace.clear': 'Quitar',
    'grace.clear.hint': 'Borra la ventana (queda como un plan sin gracia).',
    'grace.help':
      'La ventana se ancla sola al activarse una suscripción paga. Acá se mueve para ensayar; al vencer, el próximo refresh revoca la sesión.',
    'grace.empty': 'sin suscripción de este partner: no hay ventana que mover.',
    'val.expired': 'venció',
    'val.current': 'actual',
    'val.all': 'todas',
    'val.none': '—',

    // Column headers
    'col.session': 'sesión',
    'col.status': 'estado',
    'col.client': 'client',
    'col.device': 'device',
    'col.issued': 'emitida',
    'col.expires': 'expira',
    'col.type': 'tipo',
    'col.model': 'modelo',
    'col.partner': 'partner',
    'col.login': 'login',
    'col.lastSeen': 'visto',
    'col.name': 'nombre',
    'col.lastPoll': 'últ. poll',
    'col.plan': 'plan',
    'col.provider': 'proveedor',
    'col.start': 'inicio',
    'col.periodEnd': 'fin período',
    'col.date': 'fecha',
    'col.amount': 'monto',
    'col.failure': 'fallo',
    'col.created': 'creado',
    'col.used': 'usado',
    'col.version': 'versión',
    'col.when': 'cuándo',
    'col.via': 'vía',
    'col.action': 'acción',
    'col.actor': 'actor',
    'col.reason': 'motivo',
    'col.ip': 'ip',
    'col.metadata': 'metadata',
    'col.view': 'ver',

    // Empty states / hints
    'empty.identity': 'sin datos',
    'empty.entitlements': 'sin snapshot para este partner (¿sin suscripción?)',
    'empty.sessions': 'sin sesiones registradas',
    'empty.devices':
      'sin dispositivos — el registro de active_devices aún no está conectado al emitir tokens en este entorno',
    'empty.deviceCodes': 'sin device codes vivos (TTL 15 min)',
    'empty.subscriptions': 'sin suscripciones',
    'empty.payments': 'sin pagos registrados',
    'empty.emailTokens': 'sin tokens emitidos',
    'empty.tosSignatures': 'sin aceptaciones',
    'empty.audit':
      'sin eventos para este usuario con los filtros actuales — nota: el login de usuario todavía no escribe al audit log; acá aparecen bloqueos/desbloqueos admin, aceptaciones de T&C y similares',

    // Audit controls
    'audit.action': 'Acción',
    'audit.window': 'Ventana',
    'audit.w15m': '15 min',
    'audit.w1h': '1 h',
    'audit.w24h': '24 h',
    'audit.wAll': 'todo',
    'audit.refreshAll': 'Refrescar todo',

    // Actions panel
    'act.subtitle': 'fuerzan estados de ESTA cuenta',
    'act.help': 'doble click para confirmar · todo queda en el audit log',
    'act.confirm': '¿Confirmar {label}?',
    'act.running': 'Ejecutando…',
    'act.email_verify': 'Verificar email',
    'act.email_verify.hint': 'Fuerza email_verified=true sin clickear el link del mail.',
    'act.email_unverify': 'Des-verificar email',
    'act.email_unverify.hint': 'Vuelve la cuenta a "email sin verificar" — el próximo login la rebota.',
    'act.subscriptions_cancel': 'Cancelar suscripciones',
    'act.subscriptions_cancel.hint':
      'Pasa toda sub viva a cancelled y libera el alta de una nueva. No toca la pasarela.',
    'act.subscriptions_expire': 'Expirar suscripciones',
    'act.subscriptions_expire.hint': 'Igual que cancelar pero con status expired — simula el vencimiento.',
    'act.email_tokens_expire': 'Vencer links de email',
    'act.email_tokens_expire.hint': 'Expira los tokens pendientes: los links de los mails pasan a "vencido".',
    'act.entitlements_cache_invalidate': 'Purgar cache entitlements',
    'act.entitlements_cache_invalidate.hint': 'Borra el snapshot en Redis — el próximo check lee de Aurora.',
    'act.sessions_revoke_all': 'Revocar TODAS las sesiones',
    'act.sessions_revoke_all.hint':
      'Revoca access y refresh tokens de todos los dispositivos — incluida esta consola (te desloguea).',
    'act.r.emailVerified': 'email verificado',
    'act.r.emailUnverified': 'verificación revertida',
    'act.r.subs': '{n} suscripción(es) → {status}',
    'act.r.sessions': '{n} sesión(es) revocadas',
    'act.r.tokens': '{n} token(s) vencidos',
    'act.r.cache': 'cache purgado ({partners})',
    'act.r.grace': 'ventana de gracia actualizada',

    // Webhook simulator panel
    't.webhookSim': 'Simulador de webhooks',
    'wh.subtitle': 'envían un evento REAL al webhook_url del partner',
    'wh.help': 'header X-AuthSys-Simulated + flag en logs · no toca tu cuenta',
    'wh.confirm': '¿Confirmar envío de {label}?',
    'wh.sending': 'Enviando…',
    'wh.subscription.activated': 'Suscripción activada',
    'wh.subscription.activated.hint': 'subscription.activated — alta o reactivación.',
    'wh.subscription.renewed': 'Suscripción renovada',
    'wh.subscription.renewed.hint': 'subscription.renewed — renovación periódica exitosa.',
    'wh.subscription.expired': 'Suscripción expirada',
    'wh.subscription.expired.hint': 'subscription.expired — venció sin pago.',
    'wh.subscription.changed': 'Cambio de plan',
    'wh.subscription.changed.hint': 'subscription.changed — upgrade/downgrade de plan.',
    'wh.subscription.grace_period': 'Entró en gracia',
    'wh.subscription.grace_period.hint': 'subscription.grace_period — pago pendiente, en gracia.',
    'wh.user.deactivated': 'Usuario dado de baja',
    'wh.user.deactivated.hint': 'user.deactivated — cuenta eliminada.',
    'wh.device.authorized': 'Device autorizado (QR)',
    'wh.device.authorized.hint': 'device.authorized — aprobación del QR del device flow.',
    'wh.webhook.ping': 'Ping de canal',
    'wh.webhook.ping.hint': 'webhook.ping — test de canal sin semántica de negocio.',
    'wh.r.sent': '{event} enviado (event_id {id})',

    // NotFound
    'nf.message': 'Esta ruta no existe en el inspector.',
    'nf.back': 'Volver al panel',
  },
  en: {
    // Shell
    'shell.envBadge': 'dev environment',
    'shell.user': 'USER',
    'shell.partner': 'PARTNER',
    'shell.token': 'TOKEN',
    'shell.tokenTitle': 'access token expiry (renews itself with the refresh token)',
    'shell.utcTitle': 'UTC clock',
    'shell.run': 'RUN',
    'shell.hold': 'HOLD',
    'shell.pauseTitle': 'pause auto-refresh',
    'shell.resumeTitle': 'resume auto-refresh',
    'shell.logout': 'Sign out',
    'shell.langTitle': 'idioma / language',
    'shell.footer':
      'Integration tool — test environment only. Shows only the data of the account you signed in with.',
    'shell.skip': 'Skip to content',

    // Login
    'login.tagline': 'integration console — dev environment only',
    'login.email': 'Test user email',
    'login.emailPlaceholder': 'tester@your-ott.com',
    'login.password': 'Password',
    'login.partner': 'Partner ID',
    'login.submit': 'Sign in',
    'login.submitting': 'Authenticating…',
    'login.hint':
      'Sign in with the credentials of a regular user of the auth system. You will see everything AuthSys records about that account, live.',
    'login.consentTitle': 'Terms and conditions pending',
    'login.consentBody':
      'This account still has to accept version {version} of the partner’s T&C. This is the same interstitial the end user sees at login.',
    'login.consentLink': 'Read the terms ({version})',
    'login.consentAccept': 'Accept and enter',
    'login.consentAccepting': 'Recording…',
    'login.consentCancel': 'Cancel',
    'login.errGeneric': 'Login failed ({code}): {msg}',
    'login.errConsent': 'Could not record the acceptance ({code}). Retry the login.',
    'err.invalid_credentials': 'Wrong email or password.',
    'err.user_blocked': 'The account is blocked. You can verify it later in the audit panel.',
    'err.email_not_verified': 'The account email is not verified yet.',
    'err.rate_limited': 'Too many attempts. Wait a moment and retry.',
    'err.network_error': 'Could not reach the API. Is the environment running?',
    'err.partner_suspended': 'The partner is suspended in this environment.',
    'err.session_expired': 'The session expired — sign in again.',

    // SectionPanel
    'panel.loading': 'loading…',
    'panel.unavailable': 'SECTION UNAVAILABLE — {error}',
    'panel.lastFetch': 'last fetch',

    // Titles
    't.identity': 'Identity',
    't.currentSession': 'Current session',
    't.entitlements': 'Entitlements',
    't.tos': 'T&C',
    't.sessions': 'Sessions',
    't.devices': 'Active devices',
    't.deviceCodes': 'Device codes (TV)',
    't.subscriptions': 'Subscriptions',
    't.payments': 'Payments',
    't.emailTokens': 'Email tokens',
    't.tosSignatures': 'T&C signatures',
    't.audit': 'Audit log',
    't.actions': 'Test actions',

    // KV labels
    'kv.userId': 'user_id',
    'kv.display': 'display',
    'kv.email': 'email',
    'kv.status': 'status',
    'kv.emailVerif': 'email verif.',
    'kv.createdAt': 'created',
    'kv.session': 'session',
    'kv.client': 'client',
    'kv.device': 'device',
    'kv.scopes': 'scopes',
    'kv.partner': 'partner',
    'kv.subscription': 'subscription',
    'kv.plan': 'plan',
    'kv.maxStreams': 'max streams',
    'kv.maxDevices': 'max devices',
    'kv.pendingPayment': 'pending pay.',
    'kv.currentVersion': 'current',
    'kv.acceptedVersion': 'accepted',
    'kv.graceUntil': 'grace until',
    'kv.noVersions': 'no versions',

    // Common cell values
    'val.yes': 'yes',
    'val.no': 'no',
    'val.on': 'required',
    'val.off': 'not required',

    // Email verification grace window (migration 0037)
    't.grace': 'Verification grace',
    'grace.kv.gate': 'verification',
    'grace.kv.verified': 'email verified',
    'grace.kv.planHours': 'plan hours',
    'grace.kv.remaining': 'remaining',
    'grace.kv.verdict': 'login right now',
    'grace.none': 'no window',
    'grace.expired': 'expired',
    'grace.wouldPass': 'passes',
    'grace.wouldFail': 'bounces',
    'grace.presetMin': '{n} min',
    'grace.presetHour': '{n} h',
    'grace.set.hint': 'Replaces the window with a new one starting now.',
    'grace.expire': 'Expire',
    'grace.expire.hint': 'Leaves the window expired immediately — next login bounces and the refresh kills the session.',
    'grace.clear': 'Clear',
    'grace.clear.hint': 'Removes the window (same as a plan with no grace).',
    'grace.help':
      'The window is anchored automatically when a paid subscription activates. Here you move it to run tests; once expired, the next refresh revokes the session.',
    'grace.empty': 'no subscription for this partner: no window to move.',
    'val.expired': 'expired',
    'val.current': 'current',
    'val.all': 'all',
    'val.none': '—',

    // Column headers
    'col.session': 'session',
    'col.status': 'status',
    'col.client': 'client',
    'col.device': 'device',
    'col.issued': 'issued',
    'col.expires': 'expires',
    'col.type': 'type',
    'col.model': 'model',
    'col.partner': 'partner',
    'col.login': 'login',
    'col.lastSeen': 'seen',
    'col.name': 'name',
    'col.lastPoll': 'last poll',
    'col.plan': 'plan',
    'col.provider': 'provider',
    'col.start': 'start',
    'col.periodEnd': 'period end',
    'col.date': 'date',
    'col.amount': 'amount',
    'col.failure': 'failure',
    'col.created': 'created',
    'col.used': 'used',
    'col.version': 'version',
    'col.when': 'when',
    'col.via': 'via',
    'col.action': 'action',
    'col.actor': 'actor',
    'col.reason': 'reason',
    'col.ip': 'ip',
    'col.metadata': 'metadata',
    'col.view': 'view',

    // Empty states / hints
    'empty.identity': 'no data',
    'empty.entitlements': 'no snapshot for this partner (no subscription?)',
    'empty.sessions': 'no sessions on record',
    'empty.devices':
      'no devices — active_devices is not wired to token issuance in this environment yet',
    'empty.deviceCodes': 'no live device codes (15 min TTL)',
    'empty.subscriptions': 'no subscriptions',
    'empty.payments': 'no payments on record',
    'empty.emailTokens': 'no tokens issued',
    'empty.tosSignatures': 'no acceptances',
    'empty.audit':
      'no events for this user with the current filters — note: user login does not write to the audit log yet; admin blocks/unblocks, T&C acceptances and similar events show up here',

    // Audit controls
    'audit.action': 'Action',
    'audit.window': 'Window',
    'audit.w15m': '15 min',
    'audit.w1h': '1 h',
    'audit.w24h': '24 h',
    'audit.wAll': 'all',
    'audit.refreshAll': 'Refresh all',

    // Actions panel
    'act.subtitle': 'force states of THIS account',
    'act.help': 'double click to confirm · everything lands in the audit log',
    'act.confirm': 'Confirm {label}?',
    'act.running': 'Running…',
    'act.email_verify': 'Verify email',
    'act.email_verify.hint': 'Forces email_verified=true without clicking the email link.',
    'act.email_unverify': 'Un-verify email',
    'act.email_unverify.hint': 'Puts the account back to "email unverified" — the next login bounces.',
    'act.subscriptions_cancel': 'Cancel subscriptions',
    'act.subscriptions_cancel.hint':
      'Moves every live sub to cancelled and frees signing up for a new one. Does not touch the payment gateway.',
    'act.subscriptions_expire': 'Expire subscriptions',
    'act.subscriptions_expire.hint': 'Same as cancel but with status expired — simulates the period running out.',
    'act.email_tokens_expire': 'Expire email links',
    'act.email_tokens_expire.hint': 'Expires pending tokens: the links in the emails become "expired".',
    'act.entitlements_cache_invalidate': 'Purge entitlements cache',
    'act.entitlements_cache_invalidate.hint': 'Deletes the Redis snapshot — the next check reads from Aurora.',
    'act.sessions_revoke_all': 'Revoke ALL sessions',
    'act.sessions_revoke_all.hint':
      'Revokes access and refresh tokens on every device — including this console (signs you out).',
    'act.r.emailVerified': 'email verified',
    'act.r.emailUnverified': 'verification reverted',
    'act.r.subs': '{n} subscription(s) → {status}',
    'act.r.sessions': '{n} session(s) revoked',
    'act.r.tokens': '{n} token(s) expired',
    'act.r.cache': 'cache purged ({partners})',
    'act.r.grace': 'grace window updated',

    // Webhook simulator panel
    't.webhookSim': 'Webhook simulator',
    'wh.subtitle': 'sends a REAL event to the partner webhook_url',
    'wh.help': 'X-AuthSys-Simulated header + flag in logs · does not touch your account',
    'wh.confirm': 'Confirm sending {label}?',
    'wh.sending': 'Sending…',
    'wh.subscription.activated': 'Subscription activated',
    'wh.subscription.activated.hint': 'subscription.activated — new purchase or reactivation.',
    'wh.subscription.renewed': 'Subscription renewed',
    'wh.subscription.renewed.hint': 'subscription.renewed — successful periodic renewal.',
    'wh.subscription.expired': 'Subscription expired',
    'wh.subscription.expired.hint': 'subscription.expired — expired without payment.',
    'wh.subscription.changed': 'Plan changed',
    'wh.subscription.changed.hint': 'subscription.changed — plan upgrade/downgrade.',
    'wh.subscription.grace_period': 'Entered grace period',
    'wh.subscription.grace_period.hint': 'subscription.grace_period — payment pending, in grace.',
    'wh.user.deactivated': 'User deactivated',
    'wh.user.deactivated.hint': 'user.deactivated — account deleted.',
    'wh.device.authorized': 'Device authorized (QR)',
    'wh.device.authorized.hint': 'device.authorized — device flow QR approval.',
    'wh.webhook.ping': 'Channel ping',
    'wh.webhook.ping.hint': 'webhook.ping — channel test with no business meaning.',
    'wh.r.sent': '{event} sent (event_id {id})',

    // NotFound
    'nf.message': 'This route does not exist in the inspector.',
    'nf.back': 'Back to panel',
  },
};

function detectLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'es' || stored === 'en') return stored;
  } catch {
    // localStorage bloqueado — detectar del browser.
  }
  return typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('es')
    ? 'es'
    : 'en';
}

export type TFn = (key: string, vars?: Record<string, string | number>) => string;

interface I18nValue {
  lang: Lang;
  /** Locale para Date/Intl (es-AR mantiene el formato del resto del producto). */
  locale: string;
  setLang: (l: Lang) => void;
  t: TFn;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectLang());

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // solo runtime
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const table = DICT[lang];
    const t: TFn = (key, vars) => {
      let s = table[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
      }
      return s;
    };
    return { lang, locale: lang === 'es' ? 'es-AR' : 'en-US', setLang, t };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n fuera de I18nProvider');
  return ctx;
}
